import { useMemo } from 'react';
import { dateFormat, DateToDay, DayToDate } from 'shared/util'
import { DashBoardValue } from 'features/dashBoard/hooks';
import { DashBoardItemOption } from 'features/dashBoard/types';
import { GraphOptions, getAxisDef, getValueDef } from '../../common'

import {
  TrendingUp as SummaryIcon,
  Functions as TotalIcon
} from '@mui/icons-material';

import { TargetListResult } from "interface/search/targetList"

const WeekDays = [ '月', '火', '水', '木', '金', '土', '日' ]

type TimeOperation = {
  label: string
  val: (time: Date) => number
  format: (time: Date) => string | null
  longFormat: (time: Date) => string | null
  buildBars: (min: Date, max: Date) => Date[]
  topTime: (time: Date) => Date
  lastTime: (time: Date) => Date
}
export const TimeOperations: { [index: string]: TimeOperation } = {
	day: {
    label: '日',
		val: (time) => DateToDay(time),
		format: (time) => {
      const wday = WeekDays[time.getDay()]
      const day = time.getDate()
      if (day === 1) {
        const month = time.getMonth()
        if (month === 0) {
          return `${time.getFullYear()}/${month+1}/${day}(${wday})`
        }
        return `${month+1}/${day}(${wday})`
      }
      return `${day}日(${wday})`
    },
    longFormat: (time) => dateFormat(time),
    buildBars: (min, max) => {
      const minDay = DateToDay(min)
      const maxDay = DateToDay(max)
      return Array.from(
        Array((maxDay - minDay) + 1),
        (_, i) => DayToDate(minDay + i)
      )
    },
    topTime: (time: Date) => new Date(time.getFullYear(), time.getMonth(), time.getDate()),
    lastTime: (time: Date) => new Date(time.getFullYear(), time.getMonth(), time.getDate() + 1, 0, 0, 0, -1)
	},
	month: {
    label: '月',
		val: (time) => (time.getFullYear() * 12) + time.getMonth(),
		format: (time) => {
      const month = time.getMonth()
      if (month === 0) return `${time.getFullYear()}/${month + 1}`
      return `${month + 1}月`
    },
    longFormat: (time) => {
      return `${time.getFullYear()}/${time.getMonth() + 1}`
    },
    buildBars: (min, max) => {
      const minMonthYear = (min.getFullYear() * 12) + min.getMonth()
      const maxMonthYear = (max.getFullYear() * 12) + max.getMonth()
      return Array.from(
        Array((maxMonthYear - minMonthYear) + 1),
        (_, i) => {
          const year = Math.floor((minMonthYear + i) / 12)
          const month = (minMonthYear + i) % 12
          return new Date(year, month, 1)
        }
      )
    },
    topTime: (time: Date) => new Date(time.getFullYear(), time.getMonth(), 1),
    lastTime: (time: Date) => new Date(time.getFullYear(), time.getMonth() + 1, 1, 0, 0, 0, -1)
	},
	year: {
    label: '年',
		val: (time) => time.getFullYear(),
		format: (time) => `${time.getFullYear()}年`,
		longFormat: (time) => `${time.getFullYear()}年`,
    buildBars: (min, max) => {
      const minYear = min.getFullYear()
      const maxYear = max.getFullYear()
      return Array.from(
        Array((maxYear - minYear) + 1),
        (_, i) => new Date(minYear + i, 0, 1)
      )
    },
    topTime: (time: Date) => new Date(time.getFullYear(), 0, 1),
    lastTime: (time: Date) => new Date(time.getFullYear() + 1, 0, 1, 0, 0, 0, -1)
	}
}

export const TimeLineGraphOptions: GraphOptions[][] = [
  [
    {
      id: 'time',
      type: 'time',
      label: '日時'
    },
    {
      id: 'by',
      type: 'choice',
      label: '集計単位',
      prefix: 'の',
      data: Object.keys(TimeOperations).map((key) => ({
        label: TimeOperations[key].label,
        value: key
      })),
      default: 'day'
    },
    {
      id: 'value',
      type: 'value',
      prefix: 'ごとの',
      label: '値'
    },
    {
      id: 'stack',
      type: 'axis',
      prefix: 'を',
      label: '色分け',
      nullable: true,
      suffix: 'で色分け'
    }
  ], 
  [
    {
      id: 'summary',
      type: 'check',
      label: '累計',
      default: 'false',
      fromSubSource: true,
      toolBtn: <SummaryIcon fontSize="small"/>
    },
    {
      id: 'total',
      type: 'check',
      label: '全体',
      default: 'false',
      fromSubSource: true,
      toolBtn: <TotalIcon fontSize="small"/>
    }
  ]
]

type useTimeLineGraphOptionProps<T> = {
  dashBoard: DashBoardValue<T>
  options?: DashBoardItemOption[]
  targetList?: TargetListResult
}
export const useTimeLineGraphOption = <T,>({
  dashBoard: {
    configure,
    globalAxisKey
  },
  options,
  targetList
}: useTimeLineGraphOptionProps<T>) => useMemo(
  () => ({
    configure, options, targetList,
    timeDef: getAxisDef(configure, options, 'time'),
		by: options?.find((opt) => opt.id === 'by')?.value || 'day',
		valueDef: getValueDef(configure, options, 'value', targetList),
    stackDef: getAxisDef(configure, options, 'stack', globalAxisKey, targetList),
    summary: options?.find((opt) => opt.id === 'summary')?.value === 'true',
    total: options?.find((opt) => opt.id === 'total')?.value === 'true'
	}),
  [options, configure, globalAxisKey, targetList]
)
